import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";

import type { AppDispatch, RootState } from "./store";
import { db } from "../firebase";

/* ───────────────── TYPES ───────────────── */

export type Transaction = {
  id: string;
  userId: string;
  type: string;
  amount: number;
  amountCents: number;
  currency: string;
  cardId: string;
  cardBrand: string;
  cardLast4: string;
  paymentMethodId: string;
  stripeCustomerId: string;
  status: "pending" | "succeeded" | "failed";
  paymentIntentId: string | null;
  errorMessage: string | null;
  stripeErrorCode?: string | null;
  stripeDeclineCode?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  [key: string]: any;
};

export type SavedCard = {
  id: string;
  paymentMethodId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  funding: string;
  country: string;
  isDefault: boolean;
  addedAt: string | null;
};

export type Wallet = {
  userId: string;
  balance: number;
  currency: string;
  lastTopUpAmount: number | null;
  lastTopUpAt: string | null;
};

type TransactionsState = {
  transactions: Transaction[];
  savedCards: SavedCard[];
  wallet: Wallet | null;
  stripeCustomerId: string | null;
  unsubscribeTransactions?: () => void;
  unsubscribeWallet?: () => void;
  unsubscribeCards?: () => void;
  loading: boolean;
  error: string | null;
};

const initialState: TransactionsState = {
  transactions: [],
  savedCards: [],
  wallet: null,
  stripeCustomerId: null,
  unsubscribeTransactions: undefined,
  unsubscribeWallet: undefined,
  unsubscribeCards: undefined,
  loading: false,
  error: null,
};

/* ───────────────── HELPERS ───────────────── */

const toISO = (ts: any) =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseTransaction = (
  id: string,
  raw: Record<string, any>
): Transaction => {
  const { createdAt, updatedAt, ...safeRaw } = raw;

  return {
    id,
    userId: raw.userId ?? "",
    type: raw.type ?? "topup",
    amount: raw.amount ?? 0,
    amountCents: raw.amountCents ?? 0,
    currency: raw.currency ?? "eur",
    cardId: raw.cardId ?? "",
    cardBrand: raw.cardBrand ?? "",
    cardLast4: raw.cardLast4 ?? "",
    paymentMethodId: raw.paymentMethodId ?? "",
    stripeCustomerId: raw.stripeCustomerId ?? "",
    status: raw.status ?? "pending",
    paymentIntentId: raw.paymentIntentId ?? null,
    errorMessage: raw.errorMessage ?? null,
    stripeErrorCode: raw.stripeErrorCode ?? null,
    stripeDeclineCode: raw.stripeDeclineCode ?? null,
    createdAt: toISO(createdAt),
    updatedAt: toISO(updatedAt),
    ...safeRaw,
  };
};

const parseSavedCard = (
  id: string,
  raw: Record<string, any>
): SavedCard => ({
  id,
  paymentMethodId: raw.paymentMethodId ?? "",
  brand: raw.brand ?? "",
  last4: raw.last4 ?? "",
  expMonth: raw.expMonth ?? 0,
  expYear: raw.expYear ?? 0,
  funding: raw.funding ?? "",
  country: raw.country ?? "",
  isDefault: raw.isDefault ?? false,
  addedAt: toISO(raw.addedAt),
});

const parseWallet = (raw: Record<string, any>): Wallet => ({
  userId: raw.userId ?? "",
  balance: raw.balance ?? 0,
  currency: raw.currency ?? "eur",
  lastTopUpAmount: raw.lastTopUpAmount ?? null,
  lastTopUpAt: toISO(raw.lastTopUpAt),
});

const sortCards = (cards: SavedCard[]): SavedCard[] => {
  return [...cards].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    const aTime = a.addedAt ? new Date(a.addedAt).getTime() : 0;
    const bTime = b.addedAt ? new Date(b.addedAt).getTime() : 0;
    return bTime - aTime;
  });
};

/* ───────────────── LISTENERS ───────────────── */

export const listenToUserTransactions =
  (userId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!userId) return;

    const { unsubscribeTransactions } = getState().transactions;
    if (unsubscribeTransactions) {
      unsubscribeTransactions();
      dispatch(setUnsubscribeTransactions(undefined));
    }

    const q = query(
      collection(db, "transactions"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const txns = snap.docs.map((d) =>
          parseTransaction(d.id, d.data())
        );
        dispatch(setTransactions(txns));
      },
      (error) => {
        console.error("❌ transactions listener error:", error.message);
        dispatch(setError(error.message));
      }
    );

    dispatch(setUnsubscribeTransactions(unsub));
  };

export const listenToUserWallet =
  (userId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!userId) return;

    const { unsubscribeWallet } = getState().transactions;
    if (unsubscribeWallet) {
      unsubscribeWallet();
      dispatch(setUnsubscribeWallet(undefined));
    }

    const walletRef = doc(db, "wallets", userId);

    const unsub = onSnapshot(
      walletRef,
      (snap) => {
        if (!snap.exists()) {
          dispatch(
            setWallet({
              userId,
              balance: 0,
              currency: "eur",
              lastTopUpAmount: null,
              lastTopUpAt: null,
            })
          );
          return;
        }
        dispatch(setWallet(parseWallet(snap.data())));
      },
      (error) => {
        console.error("❌ wallet listener error:", error.message);
        dispatch(setError(error.message));
      }
    );

    dispatch(setUnsubscribeWallet(unsub));
  };

export const listenToUserSavedCards =
  (userId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!userId) return;

    const { unsubscribeCards } = getState().transactions;
    if (unsubscribeCards) {
      unsubscribeCards();
      dispatch(setUnsubscribeCards(undefined));
    }

    const parentRef = doc(db, "savedCards", userId);
    const cardsRef = collection(db, "savedCards", userId, "cards");

    // ✅ Track parent listener — this was the bug (orphaned listener)
    const unsubParent = onSnapshot(
      parentRef,
      (snap) => {
        if (snap.exists()) {
          dispatch(setStripeCustomerId(snap.data().stripeCustomerId ?? null));
        }
      },
      (error) => {
        console.error("❌ savedCards parent listener error:", error.message);
      }
    );

    const unsubCards = onSnapshot(
      cardsRef,
      (snap) => {
        const cards = snap.docs.map((d) =>
          parseSavedCard(d.id, d.data())
        );
        dispatch(setSavedCards(sortCards(cards)));
      },
      (error) => {
        console.error("❌ cards listener error:", error.message);
        dispatch(setError(error.message));
      }
    );

    // ✅ Combined unsubscribe so BOTH get cleaned up
    const combinedUnsub = () => {
      unsubParent();
      unsubCards();
    };

    dispatch(setUnsubscribeCards(combinedUnsub));
  };

// Start all wallet-related listeners at once
export const listenToAllWalletData =
  (userId: string) =>
  (dispatch: AppDispatch) => {
    if (!userId) return;
    dispatch(listenToUserTransactions(userId));
    dispatch(listenToUserWallet(userId));
    dispatch(listenToUserSavedCards(userId));
  };

// Cleanup all listeners
export const cleanupWalletListeners =
  () =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      unsubscribeTransactions,
      unsubscribeWallet,
      unsubscribeCards,
    } = getState().transactions;

    if (unsubscribeTransactions) unsubscribeTransactions();
    if (unsubscribeWallet) unsubscribeWallet();
    if (unsubscribeCards) unsubscribeCards();

    dispatch(setUnsubscribeTransactions(undefined));
    dispatch(setUnsubscribeWallet(undefined));
    dispatch(setUnsubscribeCards(undefined));
  };

/* ───────────────── FETCH (one-time) ───────────────── */

export const fetchUserTransactions = createAsyncThunk<
  Transaction[],
  { userId: string; maxResults?: number }
>(
  "transactions/fetchUserTransactions",
  async ({ userId, maxResults = 50 }, { rejectWithValue }) => {
    try {
      const q = query(
        collection(db, "transactions"),
        where("userId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(maxResults)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => parseTransaction(d.id, d.data()));
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch transactions");
    }
  }
);

export const fetchWalletBalance = createAsyncThunk<
  Wallet,
  string
>(
  "transactions/fetchWalletBalance",
  async (userId, { rejectWithValue }) => {
    try {
      const walletDoc = await getDoc(doc(db, "wallets", userId));

      if (!walletDoc.exists()) {
        return {
          userId,
          balance: 0,
          currency: "eur",
          lastTopUpAmount: null,
          lastTopUpAt: null,
        };
      }

      return parseWallet(walletDoc.data());
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch wallet");
    }
  }
);

export const fetchSavedCards = createAsyncThunk<
  SavedCard[],
  string
>(
  "transactions/fetchSavedCards",
  async (userId, { rejectWithValue }) => {
    try {
      const cardsSnap = await getDocs(
        collection(db, "savedCards", userId, "cards")
      );

      const cards = cardsSnap.docs.map((d) =>
        parseSavedCard(d.id, d.data())
      );

      return sortCards(cards);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch saved cards");
    }
  }
);

/* ───────────────── SLICE ───────────────── */

const transactionsSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    setTransactions: (state, action: PayloadAction<Transaction[]>) => {
      state.transactions = action.payload;
    },
    setSavedCards: (state, action: PayloadAction<SavedCard[]>) => {
      state.savedCards = action.payload;
    },
    setWallet: (state, action: PayloadAction<Wallet | null>) => {
      state.wallet = action.payload;
    },
    setStripeCustomerId: (state, action: PayloadAction<string | null>) => {
      state.stripeCustomerId = action.payload;
    },
    setUnsubscribeTransactions: (
      state,
      action: PayloadAction<(() => void) | undefined>
    ) => {
      state.unsubscribeTransactions = action.payload;
    },
    setUnsubscribeWallet: (
      state,
      action: PayloadAction<(() => void) | undefined>
    ) => {
      state.unsubscribeWallet = action.payload;
    },
    setUnsubscribeCards: (
      state,
      action: PayloadAction<(() => void) | undefined>
    ) => {
      state.unsubscribeCards = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetTransactionsState: (state) => {
      state.transactions = [];
      state.savedCards = [];
      state.wallet = null;
      state.stripeCustomerId = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload;
      })
      .addCase(fetchUserTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchWalletBalance.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWalletBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.wallet = action.payload;
      })
      .addCase(fetchWalletBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchSavedCards.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSavedCards.fulfilled, (state, action) => {
        state.loading = false;
        state.savedCards = action.payload;
      })
      .addCase(fetchSavedCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setTransactions,
  setSavedCards,
  setWallet,
  setStripeCustomerId,
  setUnsubscribeTransactions,
  setUnsubscribeWallet,
  setUnsubscribeCards,
  setError,
  resetTransactionsState,
} = transactionsSlice.actions;

export default transactionsSlice.reducer;